const isSection = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A field whose name is also the name of one of its step's Sitecore sections, from either CMS.
 *
 * Sanity stores it under its own name, like any other field. The Sitecore adapter cannot: it keeps
 * each section beside the fields it lifts out of it (`KEEP_SITECORE_FIELD_GROUPS` in
 * `libs/cms-sitecore`), and never gives a field a name a section already has. So on Sitecore the
 * field is read from inside the section it belongs to, and on Sanity, which has no sections, from
 * its own name. Five such fields in the SME energy flow:
 *
 * - the body of a radio choice step, `content` in the `content` section
 * - the opt-in text of the company details step, `content` in the `optIn` section
 * - the address step's notification, `notification` in the `content` section, next to a section
 *   called `notification`
 * - the offer step's address text, `content` in the `footer` section
 * - the summary step's note under the sales conditions, `content` in the `salesConditions` section
 *
 * The same holds for the items of a multilist, which arrive as the items they point at: on Sitecore
 * each keeps its fields in its own sections, beside the item's own `id` and `name`, while on Sanity
 * they are the item's own fields. The SME energy flow reads its products (`data` and
 * `unitPriceLabels`) and its industries (`optionValues`) this way.
 *
 * Only a field whose own name is a section's, or a multilist item's field, belongs here: any other
 * field arrives under its own name from both. The value is whatever the CMS stored, so check its type
 * before rendering it: Sanity has stored an empty rich text field as `[]`.
 *
 * Delete this once the Sitecore adapter stops keeping its sections.
 */
export const readSectionField = <T>(content: object | undefined, section: string, name: string): T | undefined => {
  const fields = content as Record<string, unknown> | undefined;
  const kept = fields?.[section];
  return (isSection(kept) ? kept[name] : fields?.[name]) as T | undefined;
};
